import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Plus, Search, Users, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'

const SOURCES = ['google', 'facebook', 'referral', 'returning', 'other', 'lead-conversion']
const CLIENT_TYPES = ['individual', 'corporate', 'non-profit', 'government', 'other']

function ClientModal({ client, onClose, onSaved }) {
  const editing = !!client?.id
  const [form, setForm] = useState({
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    company: client?.company || '',
    address: client?.address || '',
    client_type: client?.client_type || 'individual',
    source: client?.source || 'google',
    notes: client?.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }))
  }

  async function handleSave(e) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    setSaving(true)
    setError('')

    const payload = { ...form }
    let result
    if (editing) {
      result = await supabase.from('clients').update(payload).eq('id', client.id)
    } else {
      result = await supabase.from('clients').insert(payload).select().single()
    }

    if (result.error) {
      setError(result.error.message)
      setSaving(false)
    } else {
      onSaved(result.data || { ...client, ...payload })
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" data-testid="client-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>
            {editing ? 'Edit Client' : 'Create Client'}
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Company (optional)</label>
                <input className="form-input" value={form.company} onChange={e => set('company', e.target.value)} />
              </div>
            </div>

            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, Winnipeg, MB" />
            </div>

            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Client Type</label>
                <select className="form-select" value={form.client_type} onChange={e => set('client_type', e.target.value)}>
                  {CLIENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                  {SOURCES.map(s => <option key={s} value={s}>{s.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Internal Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" data-testid="client-save-button" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const SUCCESS_FEEDBACK_MS = 5000

export default function ClientsPage() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editClient, setEditClient] = useState(null)

  const loadClients = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      let query = supabase
        .from('client_summary')
        .select('*')
        .order('created_at', { ascending: false })

      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%,company.ilike.%${search}%`)
      }

      const { data, error } = await query
      if (error) throw error

      setClients(data || [])
    } catch (err) {
      console.error('Failed to load clients:', err)
      setError(err?.message || 'Failed to load clients.')
      setClients([])
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    const t = setTimeout(loadClients, 200)
    return () => clearTimeout(t)
  }, [loadClients])

  function handleSaved(savedClient) {
    setShowModal(false)
    setEditClient(null)
    setSuccess(editClient ? 'Client updated successfully.' : 'Client created successfully.')
    setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
    loadClients()
  }

  return (
    <div data-testid="clients-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">{clients.length} client{clients.length !== 1 ? 's' : ''} in system</p>
        </div>
        <button data-testid="create-client-button" className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Create Client
        </button>
      </div>

      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" data-testid="feedback-banner-success" style={{ marginBottom: 16 }}>{success}</div>}

      {/* Search */}
      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ padding: '12px 16px' }}>
          <div className="search-bar">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading…</p></div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <Users size={40} className="empty-state-icon" />
            <p>{search ? 'No clients match your search' : 'No clients yet'}</p>
            {!search && (
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                <Plus size={15} /> Create First Client
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>Quotes</th>
                  <th>Jobs</th>
                  <th>Outstanding</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{client.name}</div>
                      {client.company && (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{client.company}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 'var(--text-sm)' }}>{client.email || '—'}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{client.phone || ''}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 'var(--text-xs)', textTransform: 'capitalize', color: 'var(--color-text-secondary)' }}>
                        {client.client_type || '—'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{client.quote_count}</td>
                    <td style={{ textAlign: 'center' }}>{client.job_count}</td>
                    <td>
                      {client.outstanding_balance > 0 ? (
                        <span style={{ color: 'var(--color-red)', fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                          ${Number(client.outstanding_balance).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {format(new Date(client.created_at), 'MMM d, yyyy')}
                    </td>
                    <td>
                      <Link to={`/clients/${client.id}`} data-testid={`open-client-button-${client.id}`} className="btn btn-ghost btn-sm">
                        View <ChevronRight size={13} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {(showModal || editClient) && (
        <ClientModal
          client={editClient}
          onClose={() => { setShowModal(false); setEditClient(null) }}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
